import Nuxeo from 'nuxeo';
import ConfigurationManager from '../configuration/ConfigurationManager';
import URIBuilder from '../utils/URIBuilder';
import {
    deliveryTypeParams,
    teirOfDistributionParams,
    itemTypeParams,
    consumerTargetParams,
    packageTypeParams,
    seasonParams,
    retailerParams,
    submissionTypeParams,
    locationParams,
    areaParams,
    attractionParams,
    carouselParams,
} from '../components/nuxeo-connect/NuxeoAPI';

const NUXEO_API_PATH = '/nuxeo';

class NuxeoClient {

    constructor() {
        let uri = new URIBuilder(ConfigurationManager.getProperty('NUXEO_HOST'));
        uri.port = ConfigurationManager.getProperty('NUXEO_PORT');
        uri.path = NUXEO_API_PATH;

        this._nuxeoAPIUri = uri.toString();
        this._nuxeoAPI = new Nuxeo({ baseURL: uri.toString() })
    }

    static _instance = null;

    static instance() {
        if (!NuxeoClient._instance) {
            NuxeoClient._instance = new NuxeoClient();
        }

        return NuxeoClient._instance;
    }

    getUser() {
        return this
            ._nuxeoAPI
            .header('X-No-Basic-Header', true)
            .connect()
            .then((response) => {
                return response.user
            })
            .catch((error) => {
                this.redirectToLogin();
            });
    }

    redirectToLogin() {
        let uri = new URIBuilder(ConfigurationManager.getProperty('WBCP_HOST'));
        uri.port = ConfigurationManager.getProperty('WBCP_PORT');
        uri.path = `/redirect/index.html`;

        uri.addParam('requestedUrl', window.location.href);

        window.location.href = uri.toString();
    }

    fetchDocument(type, properties = '*') {
        let params = { type: type };
        let path = 'path/@emptyWithDefault';
        let options = {
            headers: {
                properties: properties
            }
        }
        return this
            ._nuxeoAPI
            .request(path)
            .queryParams(params)
            .execute(options);
    }

    documentEnrichment(params, properties = '*') {
        let options = {
            headers: {
                properties: properties
            }
        }
        return this
            ._nuxeoAPI
            .operation('javascript.DocumentEnrichmentQA')
            .params(params)
            .execute(options);
    }

    submitDocument(path, params, properties = '*') {
        let options = {
            headers: {
                properties: properties
            }
        }

        return this
            ._nuxeoAPI
            .repository()
            .create(path, params, options);
    }

    upcValidation(value) {
        if (value != null) {
            return true
        }
        return false
    }

    getBatchID(files, properties = '*') {
        let options = {
            headers: {
                properties: properties,
                'Repository': 'default'
            }
        }

        return this
            ._nuxeoAPI
            .request('/upload')
            .post(options);
    }

    uploadFiles(files, batchID, properties = '*') {
        var blob = new Nuxeo.Blob({ content: files[0] });

        return this._nuxeoAPI.batchUpload()
            .upload(blob)
            .then(function(res) {
                return { batchId: res.blob['upload-batch'] }
            })
            .catch(function(error) {
                console.error(error)
                throw error;
            });
    }

    getFileInfo(batchID) {
        return this
            ._nuxeoAPI
            .request(`/upload/${batchID}/info`)
            .execute()
    }

    fetchJobs(input, path, user, searchFavorites) {
        let options = {
            headers: {
                properties: '*',
                'Repository': 'default'
            }
        };
    
        input = input.toUpperCase();

        let query;

        if (searchFavorites) {
            query = {
                query: `SELECT * FROM Document WHERE 
                submission:workfrontProjectJobId like '%${input}%' AND
                ecm:path startswith '${path}' AND
                submission:isFavorite = 'true' AND
                dc:creator like '%${user}%'`
            };
        } else {
            query = {
                query: `SELECT * FROM Document WHERE 
                submission:workfrontProjectJobId like '%${input}%' AND
                ecm:path startswith '${path}' AND
                dc:creator like '%${user}%'`
            };
        };

        return this
            ._nuxeoAPI
            .request(`/search/lang/NXQL/execute`)
            .queryParams(query)
            .execute(options);
    }

    getJobInfo(jobID = '') {
        return this
            ._nuxeoAPI
            .repository()
            .fetch(jobID)
    }

    getDocument(path) {
        let options = {
            headers: {
                properties: '*',
                'Repository': 'default'
            }
        }

        return this
            ._nuxeoAPI
            .request('path/' + path)
            .get(options)
    }

    fetchContracts(licenseeCode, properties = '*') {
        let params = {
            documentSchemas: [
                "*"
            ],
            page: 0,
            pageProviderName: "ContractES",
            pageSize: 50,
            providerName: "ContractES",
            queryParams: 'ecm:parentId = "faf42931-dc9e-4d22-8555-825d7f5507e2" AND ecm:fulltext.dc:title = "*"' + (licenseeCode ? ' AND contract:licenseeCode = "' + licenseeCode + '"' : ''),
            queryParamsTmpl: 'ecm:fulltext.dc:title = "?"' + (licenseeCode ? ' AND contract:licenseeCode = "' + licenseeCode + '"' : ''),
            repository: "default",
            searchTerm: ""
        }
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('Repository.PageProvider')
            .params(params)
            .execute(options);
    }

    fetchTerritories(documentId, properties = '*') {
        let params = {
            contains: true,
            dbl10n: false,
            documentId: documentId,
            field: "contract:territory",
            lang: "en",
            localize: true,
            searchTerm: "",
        }
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('javascript.SearchField')
            .params(params)
            .execute(options);
    }

    fetchProperty(documentId, properties = '*') {
        let params = {
            contains: true,
            dbl10n: false,
            documentId: documentId,
            field: "contract:property",
            lang: "en",
            localize: true,
            searchTerm: ""
        }
        let options = {
            headers: {
                'properties': properties
            }
        }
        return this._nuxeoAPI
            .operation('javascript.SearchField')
            .params(params)
            .execute(options);
    }

    fetchTierOfDistribution(properties = '*') {
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('Directory.SuggestEntries')
            .params(teirOfDistributionParams)
            .execute(options);
    }

    fetchItemType(properties = '*') {
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('Directory.SuggestEntries')
            .params(itemTypeParams)
            .execute(options);
    }


    fetchMarketingChannels(properties = '*') {
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('Directory.SuggestEntries')
            .params(deliveryTypeParams)
            .execute(options);
    }

    fetchRetailers(properties = '*') {
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('Directory.SuggestEntries')
            .params(retailerParams)
            .execute(options);
    }

    fetchConsumerTargets(properties = '*') {
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('Directory.SuggestEntries')
            .params(consumerTargetParams)
            .execute(options);
    }

    fetchSeasons(properties = '*') {
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this
            ._nuxeoAPI
            .operation('Directory.SuggestEntries')
            .params(seasonParams)
            .execute(options);
    }

    fetchAssociatedJobs(filterData = "", properties = '*') {

        let associatedSearchParams = {
            documentSchemas: ["*"],
            repository: "default",
            providerName: 'Submission',
            pageProviderName: 'Submission',
            page: 0,
            pageSize: 20,
            searchTermTmpl: "dc:title ilike '%?%'",
            searchTerm: `dc:title ilike '%${filterData}%'`
        }

        let options = {
            headers: {
                'X-NXproperties': properties,
                'X-NXfetch.document': 'properties',
                properties: 'Public,picture'
            }
        }

        return this._nuxeoAPI
            .operation('Repository.PageProvider')
            .params(associatedSearchParams)
            .execute(options);
    }

    fetchProductDetails(documentId, properties = '*') {
        let params = {
            contains: true,
            dbl10n: false,
            documentId: documentId,
            field: "contract:productDetail",
            lang: "en",
            localize: true,
            searchTerm: ""
        }
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('javascript.SearchField')
            .params(params)
            .execute(options);
    }

    fetchPickupFiles(query, searchQuery, properties = '*') {
        let searchValue = searchQuery === '' ? '*' : searchQuery + '*';

        console.log('styleGuideQuery', query);
        console.log('input', searchQuery);

        let params = {
            "documentSchemas": ["*"],
            "repository": "default",
            "providerName": "WBCPPublishingSearchPublic",
            "pageProviderName": "WBCPPublishingSearchPublic",
            "page": 0,
            "pageSize": 20,
            "queryParamsTmpl": `${query} AND ecm:fulltext.dc:title = "?*"`,
            "searchTerm": searchQuery,
            "queryParams": `${query} AND ecm:fulltext.dc:title = "${searchValue}"`
        }
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('Repository.PageProvider')
            .params(params)
            .execute(options);
    }

    fetchAssetFileNames(query, searchQuery, properties = '*') {
        const fulltextExpr = this._buildTitleFulltextQuery(searchQuery);
        const params = {
            "documentSchemas": ["*"],
            "repository": "default",
            "providerName": "WBCPAssetSearchPublic",
            "pageProviderName": "WBCPAssetSearchPublic",
            "page": 0,
            "pageSize": 20,
            "queryParamsTmpl": query,
            "searchTerm": searchQuery,
            "queryParams": query
        };

        if (fulltextExpr) {
            const escaped = fulltextExpr.replace(/'/g, "''");
            const clause = `${query} AND ecm:fulltext.dc:title = "${escaped}"`;
            params.queryParamsTmpl = clause;
            params.queryParams = clause;
        } else if (searchQuery) {
            const escapedLike = searchQuery.replace(/'/g, "''");
            params.queryParamsTmpl = `${query} AND dc:title LIKE '%?%'`;
            params.searchTerm = searchQuery;
            params.queryParams = `${query} AND dc:title LIKE '%${escapedLike}%'`;
        }

        const options = {
            headers: {
                'X-NXproperties': properties,
            }
        };
        return this._nuxeoAPI
            .operation('Repository.PageProvider')
            .params(params)
            .execute(options);
    }

    fetchCharacters(propertyID, properties = '*') {
        let params = {
            contains: true,
            dbl10n: false,
            documentId: propertyID,
            field: "property_schema:character",
            lang: "en",
            localize: true,
            searchTerm: ""
        }
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('javascript.SearchField')
            .params(params)
            .execute(options);
    }

    fetchOriginalSeasons(propertyID, properties = '*') {
        let params = {
            contains: true,
            dbl10n: false,
            documentId: propertyID,
            lang: "en",
            localize: true,
            searchTerm: ""
        }
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('javascript.SearchSeason')
            .params(params)
            .execute(options);
    }

    fetchAssetCharacters(assetFileID, properties = '*') {
        let params = {
            assetId: assetFileID
        }
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('javascript.AssetCharacters')
            .params(params)
            .execute(options);
    }

    async _uploadFile(options, url, files) {
        const nuxeoKeyUrl = ConfigurationManager.getProperty('NUXEO_KEY_API');
        const auth = await fetch(nuxeoKeyUrl)
        const password = await auth.text()

        options['uri'] = url;
        options['method'] = 'POST'
        options['headers']['Authorization'] = atob(password)
        options['headers']['Accept'] = "application/json"

        var formdata = new FormData();
        formdata.append("File", files[0], files[0].name);

        var requestOptions = {
            method: 'POST',
            headers: options['headers'],
            body: formdata,
            redirect: 'follow'
        };

        let response = await fetch(url, requestOptions).then(res => res.text());
        response = response.replace("<html>", "");
        response = response.replace("</html>", "");
        response = JSON.parse(response);
        return response;
    }

    fetchLocations(properties = '*') {
        let options = {
            headers: {
                'X-NXproperties': properties,
                'X-NXfetch.document': 'properties',
                properties: 'Public,picture'
            }
        }
        return this._nuxeoAPI
            .operation('Repository.PageProvider')
            .params(locationParams)
            .execute(options);
    }

    fetchAreas(properties = '*') {
        let options = {
            headers: {
                'X-NXproperties': properties,
                'X-NXfetch.document': 'properties',
                properties: 'Public,picture'
            }
        }
        return this._nuxeoAPI
            .operation('Repository.PageProvider')
            .params(areaParams)
            .execute(options);
    }

    fetchAttractions(properties = '*') {
        let options = {
            headers: {
                'X-NXproperties': properties,
                'X-NXfetch.document': 'properties',
                properties: 'Public,picture'
            }
        }
        return this._nuxeoAPI
            .operation('Repository.PageProvider')
            .params(attractionParams)
            .execute(options);
    }

    fetchCarousel(properties = '*') {
        let options = {
            headers: {
                'X-NXproperties': properties,
                'X-NXfetch.document': 'properties',
                properties: '*'
            }
        }
        return this._nuxeoAPI
            .operation('Repository.PageProvider')
            .params(carouselParams)
            .execute(options)
    }

    fetchPackageTypes(properties = '*') {
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('Directory.SuggestEntries')
            .params(packageTypeParams)
            .execute(options);
    }

    fetchSubmissionTypes(properties = '*') {
        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this._nuxeoAPI
            .operation('Directory.SuggestEntries')
            .params(submissionTypeParams)
            .execute(options);
    }

    _buildTitleFulltextQuery(searchQuery = '') {
        if (!searchQuery) {
            return '';
        }

        if (/[._]/.test(searchQuery)) {
            const trimmed = searchQuery.trim();
            return trimmed || '';
        }

        const normalized = searchQuery
            .toUpperCase()
            .replace(/[^A-Z0-9]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

        if (!normalized) {
            return '';
        }

        const expanded = [];
        normalized.split(' ').forEach((chunk) => {
            if (!chunk) return;
            const splitChunk = chunk
                .replace(/([A-Z])(\d)/g, '$1 $2')
                .replace(/(\d)([A-Z])/g, '$1 $2')
                .split(' ');
            splitChunk.forEach((part) => {
                const trimmed = part.trim();
                if (trimmed) {
                    expanded.push(trimmed);
                }
            });
        });

        const tokens = [];
        const seen = new Set();
        for (const candidate of expanded) {
            if (!candidate.length) {
                continue;
            }

            const isAlpha = /^[A-Z]+$/.test(candidate);

            if (candidate.length < 2) {
                continue;
            }

            if (isAlpha && candidate.length < 2) {
                continue;
            }

            if (seen.has(candidate)) {
                continue;
            }

            seen.add(candidate);
            tokens.push(`${candidate}*`);

            if (tokens.length === 5) {
                break;
            }
        }

        if (!tokens.length) {
            const fallback = expanded.find((token) => token.length >= 2);
            if (!fallback) {
                return '';
            }
            return `${fallback}*`;
        }

        return tokens.join(' AND ');
    }

    fetchLicensees(filterData, properties = '*') {

        let licenseeFilterParams = {
            documentSchemas: [
                "*"
            ],
            repository: "default",
            providerName: "Licensee",
            pageProviderName: "Licensee",
            page: "0",
            pageSize: "50",
            searchTermTmpl: "dc:title ilike '%?%'",
            searchTerm: `dc:title ilike '%${filterData}%'`
        }

        let options = {
            headers: {
                'X-NXproperties': properties
            }
        }
        return this
            ._nuxeoAPI
            .operation('Repository.PageProvider')
            .params(licenseeFilterParams)
            .execute(options);
    }

    updateJob(doc, properties = "*") {
        return this
            ._nuxeoAPI
            .repository()
            .update(doc);
    }

}

export default NuxeoClient;